"use server";

import { auth } from "@/auth";
import { GRP_ROLE_ADMIN } from "@/lib/grp";
import { requireRole } from "@/lib/permissions";
import {
  addAakRuleSchema,
  deleteAakRuleSchema,
  editAakRuleSchema,
} from "@/lib/schemas/aak-rules";
import { prisma } from "@/prisma";
import { ActionResult } from "@/types/server-action";
import { v4 as uuidv4 } from "uuid";

export async function addAakRule(formData: unknown): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, error: "Unauthorized" };
  if (!authorized) return { success: false, error: "Forbidden" };

  const parsed = addAakRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, error: parsed.error.flatten().fieldErrors };
  }

  const {
    saleNature,
    label,
    accountingFlag,
    deliveryNature,
    networkFlag,
    warrantyStart,
  } = parsed.data;

  const existing = await prisma.aakRule.findFirst({
    where: { saleNature, deletedAt: null },
  });

  if (existing) {
    return {
      success: false,
      error: `Une règle active existe déjà pour la nature de vente "${saleNature}"`,
    };
  }

  try {
    await prisma.aakRule.create({
      data: {
        saleNature,
        label,
        accountingFlag,
        deliveryNature,
        networkFlag,
        warrantyStart: warrantyStart ?? null,
        isActive: true,
        version: 1,
        versionGroupId: uuidv4(),
      },
    });

    return { success: true };
  } catch (err) {
    console.error(err);
    return {
      success: false,
      error: "Erreur lors de la création de la règle AAK",
    };
  }
}

export async function editAakRule(formData: unknown): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, error: "Unauthorized" };
  if (!authorized) return { success: false, error: "Forbidden" };

  const parsed = editAakRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, error: parsed.error.flatten().fieldErrors };
  }

  const {
    id,
    saleNature,
    label,
    accountingFlag,
    deliveryNature,
    networkFlag,
    warrantyStart,
    isActive,
    versionNote,
  } = parsed.data;

  const existing = await prisma.aakRule.findFirst({
    where: { id, deletedAt: null },
  });

  if (!existing) {
    return { success: false, error: "Règle introuvable" };
  }

  if (saleNature !== existing.saleNature) {
    const conflict = await prisma.aakRule.findFirst({
      where: { saleNature, deletedAt: null },
    });
    if (conflict) {
      return {
        success: false,
        error: `Conflit : une règle active existe déjà pour la nature de vente "${saleNature}"`,
      };
    }
  }

  try {
    await prisma.$transaction([
      prisma.aakRule.update({
        where: { id },
        data: { deletedAt: new Date(), isActive: false },
      }),
      prisma.aakRule.create({
        data: {
          saleNature,
          label,
          accountingFlag,
          deliveryNature,
          networkFlag,
          warrantyStart: warrantyStart ?? null,
          isActive,
          version: existing.version + 1,
          versionGroupId: existing.versionGroupId,
          versionNote: versionNote ?? null,
        },
      }),
    ]);

    return { success: true };
  } catch (err) {
    console.error(err);
    return {
      success: false,
      error: "Erreur lors de la modification de la règle AAK",
    };
  }
}

export async function deleteAakRule(formData: unknown): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, error: "Unauthorized" };
  if (!authorized) return { success: false, error: "Forbidden" };

  const parsed = deleteAakRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, error: parsed.error.flatten().fieldErrors };
  }

  try {
    await prisma.aakRule.update({
      where: { id: parsed.data.id },
      data: { deletedAt: new Date(), isActive: false },
    });

    return { success: true };
  } catch (err) {
    console.error(err);
    return {
      success: false,
      error: "Erreur lors de la suppression de la règle AAK",
    };
  }
}
